import { AssistantService } from '@/api/services/assistant.service';
import type { TAssistantErrorBody } from '@/interfaces/assistant.interface';
import type { ICartadigitalAdminHostToolDeps } from '../tools/tool-types';
import { buildScopeKey } from '../tools/tool-scope';

/** After a failed request, writes stay off this long before asking again. */
export const CONFIG_RETRY_AFTER_MS = 60_000;

/**
 * A config older than this is asked again, so the switches (writes, and the
 * ops and tools turned off) reach panels that stay open (plan P0-10).
 */
export const CONFIG_MAX_AGE_MS = 5 * 60_000;

/**
 * The welcome waits this long for the config; then it shows what is known
 * (the read-only welcome while it is unknown) and the request keeps running.
 */
export const CONFIG_WELCOME_WAIT_MS = 1_500;

/**
 * Requests and failures by `userId|scopeKey`: never shared or blocked across
 * users. An answer counts only while its request is still the one listed
 * here: a reset forgets it, and an `ASSISTANT_WRITES_OFF` answer replaces it.
 */
const inflight = new Map<string, Promise<void>>();
const failedUntil = new Map<string, number>();

type TConfigDeps = Pick<
  ICartadigitalAdminHostToolDeps,
  'restaurantStore' | 'assistantStore' | 'authStore'
>;

/** The member, store and branch a config request is for. */
interface IConfigTarget {
  key: string;
  userId: number;
  restaurantId: number;
  branchId: number;
  /** `userId|scopeKey`, the key of `inflight` and `failedUntil`. */
  requestKey: string;
}

/** `r{rid}-b{bid}` of the current store and branch; null outside a store. */
export function currentAssistantScopeKey(
  deps: Pick<TConfigDeps, 'restaurantStore'>
): string | null {
  const restaurantId = deps.restaurantStore.restaurant?.id;
  const branchId = deps.restaurantStore.selectedBranch?.id;
  return typeof restaurantId === 'number' && typeof branchId === 'number'
    ? buildScopeKey({ restaurantId, branchId })
    : null;
}

/** The current member, store and branch; null outside a store or logged out. */
function configTarget(deps: TConfigDeps): IConfigTarget | null {
  const key = currentAssistantScopeKey(deps);
  const userId = deps.authStore.user?.id;
  const restaurantId = deps.restaurantStore.restaurant?.id;
  const branchId = deps.restaurantStore.selectedBranch?.id;
  if (
    !key ||
    typeof userId !== 'number' ||
    typeof restaurantId !== 'number' ||
    typeof branchId !== 'number'
  )
    return null;
  return {
    key,
    userId,
    restaurantId,
    branchId,
    requestKey: `${userId}|${key}`,
  };
}

/** The stored config was read less than `CONFIG_MAX_AGE_MS` ago. */
function isConfigFresh(store: TConfigDeps['assistantStore']): boolean {
  const age = Date.now() - store.configLoadedAt;
  return age >= 0 && age < CONFIG_MAX_AGE_MS;
}

/**
 * Loads `GET assistant/config` of the current member, store and branch into
 * the assistant store (plan §9.1 step 4), with one request for concurrent
 * callers, and asks again once it is older than `CONFIG_MAX_AGE_MS` or was
 * marked old after an `ASSISTANT_WRITES_OFF` answer (plan P0-10). Nobody
 * waits for that refresh: the cached config stays in use until the answer
 * arrives. A failure without a cached config leaves writes off (the gate
 * only reads the config of the current key); either way it is asked again
 * after a minute. An answer that arrives after the member, store or branch
 * changed is dropped: the ops depend on the member's role. So is an answer
 * whose request was replaced (see `inflight`).
 */
export function ensureAssistantConfig(deps: TConfigDeps): Promise<void> {
  const target = configTarget(deps);
  if (!target) return Promise.resolve();
  const { key, userId, restaurantId, branchId, requestKey } = target;
  const cached =
    deps.assistantStore.configKey === key && !!deps.assistantStore.config;
  if (cached && isConfigFresh(deps.assistantStore)) return Promise.resolve();

  if ((failedUntil.get(requestKey) ?? 0) > Date.now()) return Promise.resolve();
  const pending = inflight.get(requestKey);
  if (pending) return cached ? Promise.resolve() : pending;

  const request = AssistantService.getConfig(restaurantId, branchId)
    .then(
      (config) => {
        if (inflight.get(requestKey) !== request) return;
        failedUntil.delete(requestKey);
        if (
          currentAssistantScopeKey(deps) === key &&
          deps.authStore.user?.id === userId
        )
          deps.assistantStore.setConfig(key, config);
      },
      (error: unknown) => {
        if (inflight.get(requestKey) === request)
          failedUntil.set(requestKey, Date.now() + CONFIG_RETRY_AFTER_MS);
        console.warn('[assistant] config unavailable: writes stay off', error);
      }
    )
    .finally(() => {
      if (inflight.get(requestKey) === request) inflight.delete(requestKey);
    });
  inflight.set(requestKey, request);
  return cached ? Promise.resolve() : request;
}

/**
 * `ensureAssistantConfig`, waited for at most `maxWaitMs` (the welcome has
 * no timeout of its own, plan §3.4). The request keeps running and fills the
 * store when it answers.
 */
export function ensureAssistantConfigWithin(
  deps: TConfigDeps,
  maxWaitMs: number
): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, maxWaitMs);
    void ensureAssistantConfig(deps).then(() => {
      clearTimeout(timer);
      resolve();
    });
  });
}

/**
 * Forgets the requests and failures (another user logged in, or the user
 * logged out); answers still on their way are dropped.
 */
export function resetAssistantConfigRequests(): void {
  inflight.clear();
  failedUntil.clear();
}

/**
 * After an `ASSISTANT_WRITES_OFF` answer a switch changed since the config
 * was read: the config is marked old and asked again now (plan P0-10), so
 * the tools offered next follow the switch. A request already on its way
 * may have been answered before the switch flipped, and a failure a moment
 * ago must not hold this one back: both are forgotten (that answer is
 * dropped) and a new request is sent.
 */
export function refreshAssistantConfigOnRefusal(
  body: TAssistantErrorBody | null,
  deps: TConfigDeps
): void {
  if (body?.code !== 'ASSISTANT_WRITES_OFF') return;
  deps.assistantStore.expireConfig();
  const target = configTarget(deps);
  if (!target) return;
  inflight.delete(target.requestKey);
  failedUntil.delete(target.requestKey);
  void ensureAssistantConfig(deps);
}
